import { LayoutSizeContext } from 'App';
import ConditionIcons from 'features/Weather/components/ConditionIcons/ConditionIcons';
import useLocation from 'hooks/useLocation';
import { useContext, useState } from 'react';
import { useGetHourlyForecastQuery } from 'services/weather/weather.api';
import { ErrorMessage } from '../../../../../../components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from '../../../../../../components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from '../../../../../../components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from '../../../../components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from '../../../../components/WeatherWidgetSelector/WeatherWidgetSelector';
import { weatherDetails } from '../../config/forecast.config';
import { getWeatherTimeString } from '../../utils/forecast.utils';
import { layoutConfig, route } from './config/hourlyForecastWidget.config';
import {
  getHourlyForecastTileAriaLabel,
  getHourlyForecastWidgetLayout,
} from './utils/hourlyForecastWidget.utils';

const HourlyForecastWidget = ({ layout, dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext); // baseFontSize, scaleMult, widgetWidth
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: hourlyForecast,
    isLoading: loadingHourlyForecast,
    isError: isErrorHourlyForecast,
    error: errorHourlyForecast,
    refetch: refetchHourlyForecast,
  } = useGetHourlyForecastQuery(effectiveLocation);
  const [selectedItem, setSelectedItem] = useState(0);
  const {
    arrowButtonWidth,
    arrowIconSize,
    compact1x1VisibleCount,
    compact1x2ColumnCount,
    rowTileIconSize,
    tileIconSize,
    tileTextStyle,
  } = getHourlyForecastWidgetLayout({ containerSizePx, layoutSize, dimensions });
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const visibleNumOfItems = config.visibleCount({
    compact1x1VisibleCount,
    compact1x2ColumnCount,
  });

  if (isErrorHourlyForecast && !loadingHourlyForecast) {
    return (
      <ErrorMessage
        message={errorHourlyForecast?.message ?? 'Unable to load hourly forecast.'}
        retryRoute={route}
        onRetry={() => refetchHourlyForecast()}
      />
    );
  }

  if (loadingHourlyForecast)
    return <WidgetLoadingAnimation widgetName={'HourlyForecast'} dimensions={dimensions} />;

  if (hourlyForecast && hourlyForecast.length === 0) {
    return (
      <section aria-label="Hourly forecast widget">
        <WidgetTimeUpdate route={route} updateIntervalMinutes={60} />
        <p className="p-3 mb-0" role="status">
          No data found.
        </p>
      </section>
    );
  }

  const selectedForecastTime = getWeatherTimeString(hourlyForecast?.[selectedItem]?.iso8601, true);

  return (
    <section aria-label="Hourly forecast widget" className={config.wrapperClassName}>
      <WidgetTimeUpdate route={route} compact={config.compactTimeUpdate} />
      {hourlyForecast && hourlyForecast.length && (
        <div
          className="d-flex w-100 justify-content-center align-items-center"
          style={config.contentOuterStyle}
        >
          <div className={config.contentClassName} style={config.contentStyle}>
            <div
              className="d-flex flex-row w-100 align-items-center justify-content-between overflow-hidden gap-1"
              style={{
                flex: `0 0 ${config.selectorHeight}`,
                height: config.selectorHeight,
                minHeight: 0,
              }}
            >
              <WeatherWidgetSelector
                items={hourlyForecast}
                selectedIndex={selectedItem}
                visibleNumOfItems={visibleNumOfItems}
                selectorMode={config.selectorMode}
                gridColumnCount={compact1x2ColumnCount}
                gap={config.selectorGap}
                arrowButtonWidth={arrowButtonWidth}
                arrowIconSize={arrowIconSize}
                ariaLabel="Hourly forecast times"
                previousButtonLabel="Show previous hourly forecast time"
                nextButtonLabel="Show next hourly forecast time"
                tileStyle={config.selectorTileStyle}
                onSelect={(index) => setSelectedItem(index)}
                getTileProps={(item) => ({
                  label: getWeatherTimeString(item?.iso8601, true),
                  value: item.temperature,
                  ariaLabel: getHourlyForecastTileAriaLabel(item),
                  icon: (
                    <span
                      aria-hidden="true"
                      className="d-flex h-100 w-100 align-items-center justify-content-center"
                    >
                      <ConditionIcons conditionId={parseInt(item.icon, 10)} />
                    </span>
                  ),
                  iconStyle: {
                    width: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
                    height: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
                  },
                  labelStyle: tileTextStyle,
                  valueStyle: tileTextStyle,
                })}
              />
            </div>
            <WeatherWidgetDetails
              selectedItem={hourlyForecast[selectedItem]}
              details={weatherDetails}
              ariaLabel={`Hourly forecast details${
                selectedForecastTime ? ` for ${selectedForecastTime}` : ''
              }`}
              layoutGroups={config.detailGroups} // Order to render details
              containerClassName={config.detailsClassName}
              containerStyle={config.detailsStyle({ layoutSize })}
              iconSize={config.detailsIconSize}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HourlyForecastWidget;
